import type { SegmentsOptions } from '@openx/types/targeting/customVariables.ts';
import type { UseFetchSegments } from '@openx/types/targeting/targetingHookTypes.ts';
import { useMemo } from 'react';
import { useSearchOptionsByPathQuery } from '../../../hooks/useSearchOptionsByPathQuery.ts';
import { mapSegmentsOptions } from './mapSegmentsOptions.ts';

const toSegmentIdFilter = (segmentId?: string | string[]) => {
  if (segmentId === null) return undefined;

  const value = Array.isArray(segmentId) ? segmentId.filter(Boolean).join(',') : segmentId?.trim();
  return value || undefined;
};

const toProviderIdFilter = (providerId?: string | string[]) => {
  const value = Array.isArray(providerId) ? providerId.filter(Boolean).join(',') : providerId?.trim();
  return value || undefined;
};

const toDecimalString = (value?: number) => (value == null ? undefined : String(value));

export const useFetchSegments: UseFetchSegments = ({
  account_id,
  provider_id,
  segment_id,
  phrase,
  price_cpm,
  price_pom,
  limit = 100,
  offset = 0,
}) => {
  const segmentIdFilter = toSegmentIdFilter(segment_id);
  const providerIdFilter = toProviderIdFilter(provider_id);
  const hasSegmentIdFilter = segment_id !== undefined;

  const { data, loading } = useSearchOptionsByPathQuery({
    skip: !account_id || (hasSegmentIdFilter && !segmentIdFilter),
    variables: {
      filter: {
        account_id,
        provider_id: providerIdFilter,
        segment_id: segmentIdFilter,
        q: phrase?.trim() || undefined,
        min_cpm: toDecimalString(price_cpm?.min),
        max_cpm: toDecimalString(price_cpm?.max),
        min_pom: toDecimalString(price_pom?.min != null ? price_pom.min / 100 : undefined),
        max_pom: toDecimalString(price_pom?.max != null ? price_pom.max / 100 : undefined),
        limit,
        offset,
      },
      path: 'deal.package.targeting.contextual.segments',
    },
  });

  const mappedSegments: SegmentsOptions = useMemo(
    () => (data ? mapSegmentsOptions(data.searchOptionsByPath.results) : []),
    [data]
  );

  return {
    loading,
    segments: mappedSegments,
    totalCount: data?.searchOptionsByPath.total_count ?? mappedSegments.length,
    hasNextPage: data?.searchOptionsByPath.has_next_page ?? false,
  };
};
